import React from 'react';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import apiService from '@services/apiService.ts';
import type { FileResponseItem } from '../models.ts';
import { fileService } from '@services/fileService.ts';
import { DragDropZone } from '@services/dragDropService.ts';
import ThemeToggler from '@components/ThemeToggler.tsx';
import { toastService } from '@components/Toast.tsx';
import { modalService } from '@components/Modal.tsx';
import { ImgUpload } from '@components/Images/ImgUpload.tsx';
import { FileListView } from '@components/FileListView.tsx';
import { ImgLock } from '@components/Images/ImgLock.tsx';

type SharedSpaceResponse = {
  token: string;
  id: string;
  name: string;
  sizeBytes: number;
  usedBytes: number;
  files: FileResponseItem[];
};

type SharedSpaceProps = {
  token: string;
};

@observer
class SharedSpace extends React.Component<SharedSpaceProps> {
  @observable private _data: SharedSpaceResponse | null = null;
  @observable private _loading = true;
  @observable private _isDragOver = false;
  @observable private _uploading = false;
  @observable private _uploadProgress = 0;
  @observable private _currentUploadFile = '';
  @observable private _currentUploadSize = 0;
  @observable private _uploadedFileSize = 0;
  @observable private _estimatedTimeRemaining = 0;
  @observable private _error = '';
  private _uploadSpeedHistory: Array<{ timestamp: number; uploadedSize: number }> = [];
  private _speedUpdateTimer: number | null = null;
  private _dragZone: DragDropZone;

  constructor(props: SharedSpaceProps) {
    super(props);
    makeObservable(this);
    this._dragZone = new DragDropZone({
      onActiveChange: (active) => { this._isDragOver = active; },
      onDrop: (files) => { void this._uploadFiles(Array.from(files)); },
    });
  }

  async componentDidMount() {
    await this._load();
    document.getElementsByClassName('ui-loader-container')[0]?.remove();
    window.addEventListener('beforeunload', this._onBeforeUnload);
    this._dragZone.attach();
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this._onBeforeUnload);
    this._dragZone.detach();
    if (this._speedUpdateTimer) clearInterval(this._speedUpdateTimer);
  }

  render() {
    if (this._loading) {
      return <div id="sharePage" className="share-container">Loading shared space...</div>;
    }

    if (this._error) {
      return (
        <div id="sharePage" className="share-container">
          <div className="share-box" style={{ textAlign: 'center' }}>
            <h1>{this._error}</h1>
          </div>
        </div>
      );
    }

    if (!this._data) return null;

    const visibleFiles = this._data.files.filter(f => !f.isFolder && !f.isRemoved);
    const pct = this._data.sizeBytes > 0
      ? Math.min(100, (this._data.usedBytes / this._data.sizeBytes) * 100)
      : 0;

    return (
      <div id="sharePage" className="share-container">
        <div
          className={`dropzone-overlay ${this._isDragOver ? 'active' : ''}`}
        >
          <div className="dropzone-content">
            <div className="icon"><ImgUpload size={64} /></div>
            <h2>Drop anywhere!</h2>
            <p>Release to upload your files</p>
          </div>
        </div>

        <div className="share-box" style={{ maxWidth: '900px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <a href="/" style={{ textDecoration: 'none', color: 'inherit', fontSize: '18px', fontWeight: 600 }}><ImgLock /> FileVault</a>
            <ThemeToggler />
          </div>
          <h1 style={{ marginTop: 0 }}>{this._data.name}</h1>
          <div style={{ opacity: 0.8, marginBottom: '10px' }}>
            {fileService.formatFileSize(this._data.usedBytes)} / {fileService.formatFileSize(this._data.sizeBytes)} used
          </div>
          <div className="header-storage-bar" style={{ marginBottom: '14px' }}>
            <div className="header-storage-bar-fill" style={{ width: `${pct}%` }} />
          </div>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button className="btn btn-primary" onClick={this._onPickFiles} disabled={this._uploading}>Upload files</button>
            <button className="btn btn-secondary" onClick={this._copyLink}>Copy share link</button>
          </div>

          {this._uploading && (
            <div style={{ marginBottom: '12px', padding: '8px', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
              <div style={{ fontSize: '12px', marginBottom: '2px', fontWeight: 600 }}>Uploading: {this._currentUploadFile}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                {fileService.formatFileSize(this._uploadedFileSize)} / {fileService.formatFileSize(this._currentUploadSize)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                <span>{Math.round(this._uploadProgress)}%</span>
                {this._estimatedTimeRemaining > 0 && (
                  <span style={{ color: 'var(--text-muted)' }}>ETA: {this._formatTime(this._estimatedTimeRemaining)}</span>
                )}
              </div>
              <div className="header-storage-bar">
                <div className="header-storage-bar-fill" style={{ width: `${this._uploadProgress}%` }} />
              </div>
            </div>
          )}

          <FileListView
            files={visibleFiles}
            activeUploads={[]}
            removingIds={new Set<string>()}
            renderText={(text) => text ?? ''}
            onPreviewFile={(file) => {
              window.open(file.customName ? `/f/${file.customName}` : `/file/download/${file.id}`, '_blank');
            }}
            onDeleteFile={(file) => {
              void this._onDelete(file.id);
            }}
            onPermanentDelete={(file) => {
              void this._onDelete(file.id);
            }}
            onManageExpiration={() => {
              toastService.info('Manage expiration is not available in shared space', 'Shared Space');
            }}
            onCustomize={() => {
              toastService.info('Customize share page is not available in shared space', 'Shared Space');
            }}
            onShareCode={() => {}}
          />
          {visibleFiles.length === 0 && <div style={{ padding: '16px', opacity: 0.7 }}>No files yet</div>}
        </div>
      </div>
    );
  }

  private async _load() {
    runInAction(() => {
      this._loading = true;
      this._error = '';
    });
    try {
      const data = await apiService.get<SharedSpaceResponse>(`/api/file/share-space/${this.props.token}`);
      runInAction(() => {
        this._data = data;
      });
    } catch {
      runInAction(() => {
        this._error = 'Shared space not found';
      });
    } finally {
      runInAction(() => {
        this._loading = false;
      });
    }
  }

  private _onPickFiles = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = async () => {
      if (input.files && input.files.length > 0) {
        await this._uploadFiles(Array.from(input.files));
      }
    };
    input.click();
  };

  private async _uploadFiles(files: File[]) {
    runInAction(() => {
      this._uploading = true;
      this._uploadProgress = 0;
      this._currentUploadFile = '';
      this._currentUploadSize = 0;
      this._uploadedFileSize = 0;
    });

    try {
      let totalSize = 0;
      let uploadedSize = 0;
      for (const file of files) {
        totalSize += file.size;
      }

      for (const file of files) {
        runInAction(() => {
          this._currentUploadFile = file.name;
          this._currentUploadSize = file.size;
          this._uploadedFileSize = 0;
        });

        const created = await apiService.post<{ id: string; fileName: string; fileSize: number }>(
          `/api/file/share-space/${this.props.token}/file`,
          { fileName: file.name, fileSize: file.size }
        );

        const chunkSize = 1024 * 1024;
        let offset = 0;
        while (offset < file.size) {
          const end = Math.min(offset + chunkSize, file.size);
          const chunk = file.slice(offset, end);
          const formData = new FormData();
          formData.append('file', chunk, file.name);
          formData.append('fileName', file.name);
          formData.append('fileSize', file.size.toString());

          const headers: Record<string, string> = {};
          if (file.size > chunkSize) {
            headers['Content-Range'] = `bytes ${offset}-${end - 1}/${file.size}`;
          }

          const response = await fetch(`/api/file/share-space/${this.props.token}/file/${created.id}`, {
            method: 'PUT',
            headers,
            body: formData,
          });

          if (!response.ok) {
            throw new Error(await response.text());
          }

          offset = end;
          uploadedSize += chunk.size;
          runInAction(() => {
            this._uploadedFileSize = offset;
            this._uploadProgress = totalSize > 0 ? (uploadedSize / totalSize) * 100 : 0;
            this._recordUploadSpeed(uploadedSize, totalSize);
          });
        }
      }
      await this._load();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      toastService.error(message, 'Shared Space');
    } finally {
      runInAction(() => {
        this._uploading = false;
        this._uploadProgress = 0;
        this._currentUploadFile = '';
        this._currentUploadSize = 0;
        this._uploadedFileSize = 0;
        this._estimatedTimeRemaining = 0;
      });
      this._uploadSpeedHistory = [];
      if (this._speedUpdateTimer) clearInterval(this._speedUpdateTimer);
      this._speedUpdateTimer = null;
    }
  }

  @action.bound
  private _onBeforeUnload(e: BeforeUnloadEvent) {
    if (this._uploading) {
      e.preventDefault();
      e.returnValue = '';
      return '';
    }
  }

  private _recordUploadSpeed(uploadedSize: number, totalSize: number) {
    const now = Date.now();
    this._uploadSpeedHistory.push({ timestamp: now, uploadedSize });
    
    // Keep only last 10 seconds of history
    const tenSecondsAgo = now - 10000;
    this._uploadSpeedHistory = this._uploadSpeedHistory.filter(entry => entry.timestamp > tenSecondsAgo);
    
    // Update ETA every 2 seconds
    if (!this._speedUpdateTimer) {
      this._speedUpdateTimer = window.setInterval(() => {
        this._updateEstimatedTime(totalSize);
      }, 2000);
    }
  }

  private _updateEstimatedTime(totalSize: number) {
    if (this._uploadSpeedHistory.length < 2) return;
    
    const firstEntry = this._uploadSpeedHistory[0];
    const lastEntry = this._uploadSpeedHistory[this._uploadSpeedHistory.length - 1];
    const timeDiffSeconds = (lastEntry.timestamp - firstEntry.timestamp) / 1000;
    const sizeDiff = lastEntry.uploadedSize - firstEntry.uploadedSize;
    
    if (timeDiffSeconds > 0 && sizeDiff > 0) {
      const speedBytesPerSecond = sizeDiff / timeDiffSeconds;
      const remainingBytes = totalSize - lastEntry.uploadedSize;
      const estimatedSeconds = Math.ceil(remainingBytes / speedBytesPerSecond);
      
      runInAction(() => {
        this._estimatedTimeRemaining = Math.max(0, estimatedSeconds);
      });
    }
  }

  private _formatTime(seconds: number): string {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  }

  @action.bound
  private async _onDelete(fileId: string) {
    const confirmed = await modalService.showConfirmation('Are you sure you want to delete this file from the shared space?', 'Delete file?');
    if (!confirmed) return;

    try {
      await apiService.delete(`/api/file/share-space/${this.props.token}/file/${fileId}`);
      await this._load();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Delete failed';
      toastService.error(message, 'Shared Space');
    }
  }

  @action.bound
  private _copyLink() {
    navigator.clipboard.writeText(window.location.href)
      .then(() => toastService.info('Link copied', 'Shared Space'))
      .catch(() => toastService.error('Cannot copy link', 'Shared Space'));
  }
}

export default SharedSpace;
